import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

export interface IConciliacaoVenda {
     solicitacao_id: number;
     numero_solicitacao: number;
     fk_id_venda?: number;
     codigo_conciliacao_solicitacao: string;
     valor_solicitacao: number;
     codigo_estado_solicitacao: number;
     timestamp_criacao_solicitacao: string;
     sistema_origem_nome: string;
     sistema_origem_prefixo: string;
     organizacao_nome: string;
     pagamento_id?: number;
     numero_pagamento?: number;
     valor_original_pagamento?: number;
     valor_liquido_recebedor?: number;
     timestamp_pagamento?: string;
     codigo_estado_pagamento?: number;
     status_conciliacao: 'PENDENTE' | 'CONCILIADO' | 'DIVERGENTE';
     diferenca_valor?: number;
}

/**
 * Serviço para a view de conciliação de vendas
 * Mostra o status de conciliação entre solicitações e pagamentos
 * Corresponde à view 'vw_conciliacao_vendas' no banco de dados
 */
export class ViewConciliacaoVendasService extends BaseMultiTenantService<IConciliacaoVenda> {
     constructor() {
          super('vw_conciliacao_vendas', [], 'organizacao_nome');
     }

     /**
      * Busca pendências de conciliação por organização
      */
     async getPendentes(organizationId: number) {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('status_conciliacao', 'PENDENTE')
               .order('timestamp_criacao_solicitacao', { ascending: false });
     }

     /**
      * Busca divergências de conciliação
      */
     async getDivergentes(organizationId: number) {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('status_conciliacao', 'DIVERGENTE')
               .order('timestamp_criacao_solicitacao', { ascending: false });
     }

     /**
      * Busca conciliações por status
      */
     async getByStatus(organizationId: number, status: 'PENDENTE' | 'CONCILIADO' | 'DIVERGENTE') {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('status_conciliacao', status)
               .order('timestamp_criacao_solicitacao', { ascending: false });
     }

     /**
      * Busca por código de conciliação
      */
     async getByCodigo(organizationId: number, codigo: string) {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('codigo_conciliacao_solicitacao', codigo)
               .single();
     }

     /**
      * Busca por período
      */
     async getByPeriodo(organizationId: number, dataInicio: string, dataFim: string) {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .gte('timestamp_criacao_solicitacao', dataInicio)
               .lte('timestamp_criacao_solicitacao', dataFim)
               .order('timestamp_criacao_solicitacao', { ascending: false });
     }

     /**
      * Estatísticas de conciliação
      */
     async getEstatisticas(organizationId: number) {
          const { data } = await this.client
               .from(this.tableName)
               .select('status_conciliacao, valor_solicitacao.sum(), diferenca_valor.sum()')
               .eq(this.organizationIdField, organizationId);

          return data;
     }
}

// Instância singleton para uso no sistema
export const viewConciliacaoVendas = new ViewConciliacaoVendasService();
